/**
 * Translate NMI / Vector Payments gateway responses into customer-friendly
 * toast messages.
 *
 * NMI returns:
 *   - `response`            "1" approved · "2" declined · "3" error
 *   - `response_code`       3-digit code (200..464 — see ref table below)
 *   - `responsetext`        free-form vendor string ("Do Not Honor",
 *                            "Activity limit exceeded", "DECLINE", etc.)
 *
 * The vendor strings are noisy and vary by issuer; the numeric codes are
 * the stable contract. We prefer matching on `response_code` and fall back
 * to regex on `responsetext` so issuer-specific phrasings still land on the
 * right friendly message.
 *
 * Response-code reference:
 *   https://www.merchantware.com/wp-content/uploads/2019/03/
 *     NMI-Response-Codes.pdf
 * (Vector Payments uses the same table — they're an NMI reseller.)
 *
 * Return shape:
 *   { customer, dev, action, severity }
 *     customer   — what we show in the toast / inline error
 *     dev        — the raw vendor string (for console logs + Sentry)
 *     action     — short hint about what the customer should do next, or null
 *     severity   — 'decline' | 'gateway' | 'cardholder_input' | 'temporary'
 *                  — lets the UI pick the toast variant (error vs warning)
 */

/**
 * Security-code (CVV/CID) mismatch. Brand-neutral on purpose: the error body
 * doesn't carry the card brand, and American Express puts its 4-digit code
 * (CID) on the FRONT. The old "on the back of your card" copy sent Amex
 * holders to the wrong digits (Denaira 9L9961, 10-14: "CID FORMAT ERROR").
 */
const SECURITY_CODE_MISMATCH = {
  customer:
    "The security code doesn't match. On most cards it's the 3 digits on the back; on American Express it's the 4-digit code on the front, above the card number. Please re-check and try again.",
  severity: 'cardholder_input',
  action: 'Re-check the security code',
};

/**
 * Per-code overrides. Keys are the 3-digit NMI `response_code` as a string
 * (we keep it stringy because NMI returns it that way and JS lookup on
 * mixed types is a trap).
 */
const CODE_MAP = {
  '200': {
    customer: 'Your card was declined by the bank. Please try a different card.',
    severity: 'decline',
    action: 'Try another card',
  },
  '201': {
    customer:
      "Your bank wouldn't authorize this charge. This usually means your card is locked or your bank wants to verify the purchase — try contacting them, or use a different card.",
    severity: 'decline',
    action: 'Contact your bank or try another card',
  },
  '202': {
    customer:
      "There aren't enough funds available on this card. Please try a different card or payment method.",
    severity: 'decline',
    action: 'Use a different card',
  },
  '203': {
    customer:
      'This charge would exceed the limit on your card. Please use a different card.',
    severity: 'decline',
    action: 'Use a different card',
  },
  '204': {
    customer:
      "Your card doesn't allow this type of transaction. Please use a different card.",
    severity: 'decline',
    action: 'Use a different card',
  },
  '220': {
    customer: 'Please double-check your card number, expiration date, and CVV — one of them looks off.',
    severity: 'cardholder_input',
    action: 'Re-check your card details',
  },
  '221': {
    customer: "We don't recognize that card's issuer. Please use a Visa, Mastercard, Discover, or American Express card.",
    severity: 'cardholder_input',
    action: 'Try a different card',
  },
  '223': {
    customer: 'That card has expired. Please use a different card.',
    severity: 'cardholder_input',
    action: 'Use a different card',
  },
  '224': {
    customer: 'The expiration date you entered is invalid. Please check it and try again.',
    severity: 'cardholder_input',
    action: 'Re-check expiration date',
  },
  '225': SECURITY_CODE_MISMATCH,
  '240': {
    customer:
      'Your bank needs to verify this purchase before it can go through. Please call the number on the back of your card, then try again.',
    severity: 'decline',
    action: 'Contact your bank',
  },
  '250': {
    customer:
      'This card has been reported lost or stolen. Please use a different card. If this is unexpected, contact your bank.',
    severity: 'decline',
    action: 'Use a different card',
  },
  '251': {
    customer:
      'This card has been reported lost or stolen. Please use a different card.',
    severity: 'decline',
    action: 'Use a different card',
  },
  '252': {
    customer:
      'This card has been reported lost or stolen. Please use a different card.',
    severity: 'decline',
    action: 'Use a different card',
  },
  '253': {
    customer:
      'This card has been flagged. Please use a different card.',
    severity: 'decline',
    action: 'Use a different card',
  },
  '260': {
    customer:
      'Your card was declined. Please try a different card or contact your bank.',
    severity: 'decline',
    action: 'Try another card',
  },
  '261': {
    customer:
      'Your bank declined the recurring portion of this charge. Please use a different card to set up your plan.',
    severity: 'decline',
    action: 'Use a different card',
  },
  '262': {
    customer:
      'Your bank declined the recurring portion of this charge. Please use a different card.',
    severity: 'decline',
    action: 'Use a different card',
  },
  '263': {
    customer:
      'This card has been replaced. Please use your current card.',
    severity: 'cardholder_input',
    action: 'Use your current card',
  },
  '264': {
    customer:
      'Your bank asked us to retry this charge in a few days. Please try again later or use a different card.',
    severity: 'temporary',
    action: 'Try again later',
  },
  '300': {
    customer: "We couldn't process this transaction. Please try again, or contact us at defera.app/contact if it keeps happening.",
    severity: 'gateway',
    action: 'Try again',
  },
  '400': {
    customer: 'Something went wrong on our payment processor. Please try again in a moment.',
    severity: 'gateway',
    action: 'Try again',
  },
  '410': {
    customer: 'Payments are temporarily unavailable. Please try again in a few minutes — our team has been notified.',
    severity: 'gateway',
    action: 'Try again later',
  },
  '411': {
    customer: 'Payments are temporarily unavailable. Please try again in a few minutes — our team has been notified.',
    severity: 'gateway',
    action: 'Try again later',
  },
  '420': {
    customer: "We couldn't reach the payment processor. Please check your internet and try again.",
    severity: 'temporary',
    action: 'Try again',
  },
  '421': {
    customer:
      "Your bank couldn't be reached right now. Please try again in a moment, or use a different card.",
    severity: 'temporary',
    action: 'Try again',
  },
  '430': {
    customer:
      'It looks like this charge was already submitted. Please refresh and check your trips — if it doesn\'t show up in a minute, contact us at defera.app/contact.',
    severity: 'temporary',
    action: 'Refresh and check My Trips',
  },
  '461': {
    customer: "We don't accept that type of card. Please use a Visa, Mastercard, Discover, or American Express card.",
    severity: 'cardholder_input',
    action: 'Try a different card type',
  },
};

/**
 * Regex fallbacks on the raw `responsetext`. Order matters — first match wins.
 * Keep these case-insensitive and anchored loosely; NMI / issuer phrasing
 * varies subtly across banks.
 */
const TEXT_RULES = [
  {
    re: /do(?:\s+not|n[' ]?t)\s+honor/i,
    msg: {
      customer:
        "Your bank wouldn't authorize this charge. This usually means your card is locked or your bank wants to verify the purchase — try contacting them, or use a different card.",
      severity: 'decline',
      action: 'Contact your bank or try another card',
    },
  },
  {
    re: /insufficient\s+funds/i,
    msg: {
      customer:
        "There aren't enough funds available on this card. Please try a different card or payment method.",
      severity: 'decline',
      action: 'Use a different card',
    },
  },
  {
    re: /(activity|velocity|frequency)\s+limit\s+exceeded/i,
    msg: {
      customer:
        "We've seen too many attempts on this card recently. Please wait a few minutes, then try again or use a different card.",
      severity: 'temporary',
      action: 'Try again in a few minutes or use another card',
    },
  },
  {
    re: /expired\s+card/i,
    msg: {
      customer: 'That card has expired. Please use a different card.',
      severity: 'cardholder_input',
      action: 'Use a different card',
    },
  },
  {
    // "Invalid CVV", and Amex's "CID Verification error" / "CID FORMAT ERROR".
    re: /(invalid|incorrect)\s+(cvv2?|cvc2?|cid|security\s+code)|\b(cvv2?|cvc2?|cid)\b\W*(verification|format|mismatch)/i,
    msg: SECURITY_CODE_MISMATCH,
  },
  {
    re: /(invalid|incorrect)\s+(card\s+number|account)/i,
    msg: {
      customer: "That card number doesn't look right. Please re-enter your card and try again.",
      severity: 'cardholder_input',
      action: 'Re-enter your card',
    },
  },
  {
    re: /(pick\s*up|pickup)\s+card/i,
    msg: {
      customer:
        'This card has been flagged by your bank. Please use a different card. If this is unexpected, contact your bank.',
      severity: 'decline',
      action: 'Use a different card',
    },
  },
  {
    re: /(stolen|lost)\s+card/i,
    msg: {
      customer:
        'This card has been reported lost or stolen. Please use a different card.',
      severity: 'decline',
      action: 'Use a different card',
    },
  },
  {
    re: /suspected\s+fraud/i,
    msg: {
      customer:
        'Your bank declined this for suspected fraud. Please contact your bank to authorize the purchase, or use a different card.',
      severity: 'decline',
      action: 'Contact your bank or try another card',
    },
  },
  {
    re: /(restrict(ed)?|stop\s+payment)/i,
    msg: {
      customer:
        'Your card has restrictions on it. Please use a different card.',
      severity: 'decline',
      action: 'Use a different card',
    },
  },
  {
    re: /duplicate\s+(transaction|payment)/i,
    msg: {
      customer:
        "It looks like this charge was already submitted. Please refresh and check My Trips — if your booking isn't there in a minute, contact us at defera.app/contact.",
      severity: 'temporary',
      action: 'Refresh and check My Trips',
    },
  },
  {
    re: /(communication|gateway|processor)\s+(error|timeout|unavailable)/i,
    msg: {
      customer: "We couldn't reach the payment processor. Please try again in a moment.",
      severity: 'temporary',
      action: 'Try again',
    },
  },
  {
    re: /address|zip|postal/i,
    msg: {
      customer: "Please double-check your billing address and ZIP code, then try again.",
      severity: 'cardholder_input',
      action: 'Re-check your billing address',
    },
  },
];

/**
 * Default messages by `error_type` — last line of defense when we have
 * neither a known code nor a recognised text pattern.
 */
const TYPE_DEFAULTS = {
  card_error: {
    customer:
      'Your card was declined. Please try a different card, or contact your bank if you think this is a mistake.',
    severity: 'decline',
    action: 'Try another card',
  },
  gateway_error: {
    customer:
      "We couldn't process your card right now. Please try again in a moment — if it keeps happening, contact us at defera.app/contact.",
    severity: 'gateway',
    action: 'Try again',
  },
  idempotency_error: {
    customer:
      'Please refresh the page and try again — your card was not charged.',
    severity: 'gateway',
    action: 'Refresh and retry',
  },
};

/**
 * Translate an NMI error response into customer-friendly toast content.
 *
 * Accepts the JSON body returned by our NMI edge fns (`error_type`,
 * `gateway_response_code`, `error` = vendor responsetext) and falls back
 * gracefully when any of those are missing.
 *
 * @param {object|null|undefined} errBody  — Parsed response body from an
 *   NMI edge function. May also be a plain Error or a string for callers
 *   that only have `err.message`.
 * @param {object} [opts]
 * @param {string} [opts.defaultMessage] — Fallback when no rule fires
 *   AND `error_type` is unknown.
 * @returns {{ customer: string, dev: string, action: string|null, severity: string }}
 */
export function nmiDeclineMessage(errBody, opts = {}) {
  const fallback = opts.defaultMessage ||
    "We couldn't process your card right now. Please try again, or use a different card.";

  // Normalize the input — callers pass raw fetch JSON bodies, Error
  // instances, or plain strings.
  let body;
  if (!errBody) {
    body = {};
  } else if (typeof errBody === 'string') {
    body = { error: errBody };
  } else if (errBody instanceof Error) {
    body = { error: errBody.message };
  } else {
    body = errBody;
  }

  const rawText = String(body.error || body.responsetext || '').trim();
  const code = body.gateway_response_code != null
    ? String(body.gateway_response_code)
    : null;
  const errorType = typeof body.error_type === 'string' ? body.error_type : null;

  /**
   * Build a final return shape from a partial rule match, filling in `dev`
   * from the raw vendor text so engineers can still trace back to NMI.
   */
  /**
   * Our own "still confirming your last payment" refusal (a retry after a
   * lost answer, while the server asks NMI whether the first try went
   * through). It is not a decline, and "please try again" is the wrong advice:
   * show the server's words.
   */
  if (body.code === 'payment_processing' || errorType === 'payment_processing') {
    return {
      customer: rawText || 'Your last payment attempt is still being confirmed. Please check your trip in a few minutes before trying again.',
      severity: 'temporary',
      action: 'Check back in a few minutes',
      dev: rawText || 'payment_processing',
    };
  }

  const finish = (rule, extraDev) => ({
    customer: rule.customer,
    severity: rule.severity,
    action: rule.action ?? null,
    dev: extraDev || rawText || 'unknown',
  });

  if (code && CODE_MAP[code]) {
    return finish(CODE_MAP[code]);
  }

  if (rawText) {
    for (const rule of TEXT_RULES) {
      if (rule.re.test(rawText)) return finish(rule.msg);
    }
  }

  if (errorType && TYPE_DEFAULTS[errorType]) {
    return finish(TYPE_DEFAULTS[errorType]);
  }

  return {
    customer: fallback,
    severity: 'gateway',
    action: null,
    dev: rawText || 'unknown',
  };
}
